// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import axios from 'axios'
import { ethers, FallbackProvider, Network } from 'ethers'
import { DefiCollateralCTO, DefiType } from './CollateralSetCTO'
import { DefiData } from './defiCTO'
import { getAaveNameAndSymbol, getDefiDataAave } from './defiVaultUtilsAave'
import { getDefiDataMorpho, getMorphoNameAndSymbol } from './defiVaultUtilsMorpho'
import { getDefiDataSimulated, getSimulatedNameAndSymbol } from './defiVaultUtilsSimulated'
import { getDefiDataWildcat, getWildcatNameAndSymbol } from './defiVaultUtilsWildcat'
import { BLOCK_EXPLORER_API_CONFIG, MAINNET_INFURA_URL } from './DeploymentConfigs'
import { ChainId } from './Enums'
import { logOT } from './loggingShared'
import { PROD_BASE_DEFAULT_URL } from './DeploymentConfigsProd'
export interface TimeStructure {
  timestamp: number
  blockNumberEthereum: number
  blockNumberAvax: number
  blockNumberBase: number
}

export function differenceLessThanVariance(
  expectedExchangeRate: bigint,
  actualExchangeRate: bigint,
  resolvedVarianceBN: bigint
): boolean {
  const diff =
    expectedExchangeRate > actualExchangeRate
      ? expectedExchangeRate - actualExchangeRate
      : actualExchangeRate - expectedExchangeRate
  if (diff > resolvedVarianceBN) {
    logOT.info('differenceGreaterThanVariance', {
      tag: 'differenceGreaterThanVariance',
      diff,
      resolvedVarianceBN,
    })
  }
  return diff < resolvedVarianceBN
}
export async function getDefiNameAndSymbol(
  collateralCTO: DefiCollateralCTO
): Promise<{ name: string; symbol: string }> {
  switch (collateralCTO.defiType) {
    case DefiType.Aave:
      return getAaveNameAndSymbol(collateralCTO)
    case DefiType.Morpho:
      return getMorphoNameAndSymbol(collateralCTO)
    case DefiType.MorphoBase:
      return getMorphoNameAndSymbol(collateralCTO)
    case DefiType.Wildcat:
      return getWildcatNameAndSymbol(collateralCTO)
    case DefiType.SIMULATED:
      return getSimulatedNameAndSymbol(collateralCTO)
    default:
      throw new Error(`Unsupported defi type: ${collateralCTO.defiType}`)
  }
}
export async function getDefiData(collateralCTO: DefiCollateralCTO, timeStructure?: TimeStructure): Promise<DefiData> {
  logOT.info('getDefiData', {
    collateralCTO,
  })
  switch (collateralCTO.defiType) {
    case DefiType.Aave:
      return getDefiDataAave(collateralCTO, timeStructure)
    case DefiType.Morpho:
      return getDefiDataMorpho(collateralCTO, timeStructure)
    case DefiType.MorphoBase:
      return getDefiDataMorpho(collateralCTO, timeStructure)
    case DefiType.Wildcat:
      return getDefiDataWildcat(collateralCTO, timeStructure)
    case DefiType.SIMULATED:
      return getDefiDataSimulated(collateralCTO, timeStructure)
    default:
      throw new Error(`Unsupported defi type: ${collateralCTO.defiType}`)
  }
}
export async function getBlockNumberForTimestamp(timestamp: number, chainId: ChainId): Promise<number> {
  if (chainId === ChainId.MAINNET) {
    const { apiKey } = BLOCK_EXPLORER_API_CONFIG['mainnet']
    const url = `https://api.etherscan.io/v2/api?chainid=1&module=block&action=getblocknobytime&timestamp=${timestamp}&closest=after&apikey=${apiKey}`
    await process.nextTick(() => {})
    const response = await axios.get(url)
    return Number(response.data.result)
  }
  if (chainId === ChainId.AVALANCHE) {
    const { apiKey } = BLOCK_EXPLORER_API_CONFIG['avalanche']
    const url = `https://api.snowtrace.io/api?chainid=43114&module=block&action=getblocknobytime&timestamp=${timestamp}&closest=after&apikey=${apiKey}`
    await process.nextTick(() => {})
    const response = await axios.get(url)
    return Number(response.data.result)
  }
  if (chainId === ChainId.BASE) {
    const { apiKey } = BLOCK_EXPLORER_API_CONFIG['base']

    const url = `https://api.etherscan.io/v2/api?chainid=8453&module=block&action=getblocknobytime&timestamp=${timestamp}&closest=after&apikey=${apiKey}`
    await process.nextTick(() => {})
    const response = await axios.get(url)
    return Number(response.data.result)
  }
  throw new Error(`Unsupported chain id: ${chainId}`)
}
export const getMainnetProviderForDefi = (chainId: ChainId): ethers.Provider => {
  if (chainId === ChainId.MAINNET) {
    const providerInfura = new ethers.JsonRpcProvider(MAINNET_INFURA_URL, new Network('Ethereum', '1'), {
      staticNetwork: true,
      pollingInterval: 100,
      polling: false,
      batchMaxCount: 1,
    })
    const providerAlchemy = new ethers.JsonRpcProvider(
      'https://eth-mainnet.g.alchemy.com/v2/q9zjx0r2FcVd2jickusbV',
      undefined,
      {
        staticNetwork: true,
        pollingInterval: 100,
        polling: false,
        batchMaxCount: 1,
      }
    )

    const _fallbackProvider = new FallbackProvider(
      [
        { provider: providerInfura, priority: 1, stallTimeout: 1000, weight: 2 },
        { provider: providerAlchemy, priority: 2, stallTimeout: 100, weight: 1 },
      ],
      new Network('Ethereum', 1),
      {
        cacheTimeout: 100,
        eventQuorum: 1,
        quorum: 1,
        pollingInterval: 100,
      }
    )

    const provider = process.env.JEST_WORKER_ID ? providerInfura : providerAlchemy
    return provider
  }
  if (chainId === ChainId.BASE) {
    const providerBase = new ethers.JsonRpcProvider(PROD_BASE_DEFAULT_URL, new Network('Base', '8453'), {
      staticNetwork: true,
      pollingInterval: 100,
      polling: false,
      batchMaxCount: 1,
    })
    return providerBase
  }
  throw new Error(`Unsupported chain id: ${chainId}`)
}
